import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useNavigation, useRoute } from 'app/hooks/navigation';
import React, { useState } from 'react';
import { Pressable } from 'react-native';
import Animated, { FadeOut, LinearTransition } from 'react-native-reanimated';

import { Button } from 'app/design/primitives/button';
import { ListingType } from 'app/types/listing';
import { Trans, useTranslation } from 'react-i18next';
import { useScopedTranslation } from 'app/hooks/i18n';
import { navigateInCurrentNavigatorWithFallback } from 'app/screens/root-stack-helper';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import type { Bonification } from 'app/types/bonification';
import { PCImage } from 'app/design/primitives/image';
import { Separator } from 'app/design/primitives/text-divider';
import { useListingsToBonify, usePendingBonifications } from 'app/network/bonification';
import { useUISize } from 'app/utils/web-helpers';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight, IconNotes, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function useBonificationBanner({ enabled = true }: { enabled?: boolean } = {}) {
  const userInfo = useAtomValue(userInfoAtom);
  const route = useRoute();
  const closedBannerStorageKey =
    route.name === 'my-listings' ? StorageKey.ClosedMyListingsBonificationBanner : StorageKey.ClosedBonificationBanner;
  const [closedBonificationBanner, setClosedBonificationBanner, , bonificationBannerLoaded] = useStoredValue<boolean>(
    closedBannerStorageKey,
    false,
  );
  const [dismissed, setDismissed] = useState(false);
  const { numberOfListingsToBonify } = useListingsToBonify({
    userId: userInfo?.id,
    enabled,
    triggeredBy: 'BonificationBanner',
  });
  const dismissable = route.name !== 'management-overview';

  return {
    shouldHideBonificationBanner:
      !bonificationBannerLoaded ||
      dismissed ||
      (closedBonificationBanner && dismissable) ||
      numberOfListingsToBonify === 0,
    dismissed,
    setDismissed,
    setClosedBonificationBanner,
    dismissable,
    numberOfListingsToBonify,
  };
}

export type BonificationBannerState = ReturnType<typeof useBonificationBanner>;

function getCompactPromptLayout(routeName: string, isLargeUI: boolean, isSmallUI: boolean, dismissable: boolean) {
  const isMyListings = routeName === 'my-listings';
  const isInBanner = routeName === 'landing-page';

  return {
    bodyNamespace: routeName === 'management-overview' ? 'management' : 'all-listings',
    flex: routeName === 'management-overview' ? 1 : undefined,
    gap: isMyListings ? 'spacing.general.x2' : 'spacing.general.x4',
    marginBottom: isMyListings ? 'spacing.general.x2' : 'spacing.general.0',
    marginHorizontal: isMyListings ? 'spacing.general.x4' : 'spacing.general.0',
    overflow: isInBanner ? 'hidden' : undefined,
    paddingBottom: isMyListings || isSmallUI ? 'spacing.general.x4' : 'spacing.general.x8',
    paddingRight: isMyListings ? 'spacing.general.x8' : 'spacing.general.0',
    paddingTop: isMyListings ? 'spacing.general.x2' : !dismissable ? 'spacing.general.x4' : 'spacing.general.0',
    width: isInBanner && isLargeUI ? 550 : undefined,
  } as const;
}

function BonificationPromptContent({
  count,
  dismissable,
  setClosedBonificationBanner,
  setDismissed,
}: {
  count: number;
  dismissable: boolean;
  setClosedBonificationBanner: (value: boolean) => void;
  setDismissed: (value: boolean) => void;
}) {
  const { t } = useScopedTranslation();
  const route = useRoute();
  const navigation1 = useNavigation<'my-listings'>();
  const navigation2 = useNavigation<'one-listing'>();
  const userInfo = useAtomValue(userInfoAtom);
  const { isLargeUI, isSmallUI } = useUISize();
  const isSingle = count === 1;

  const { firstBonifiableListing } = useListingsToBonify({
    userId: userInfo?.id,
    loadFirstBonifiableListing: isSingle,
    triggeredBy: `BonificationPrompt-${route.name}`,
  });
  const { data: pendingBonifications } = usePendingBonifications({ enabled: route.name === 'my-listings' });
  const navigateToBonification = (bonification: Bonification) => {
    navigation1.navigate(
      'chat',
      {
        screen: 'listing-bonification',
        params: {
          loanId: bonification.loanId,
          bonificationId: bonification.id,
        },
      },
      { pop: true },
    );
  };

  const onPress = () => {
    if (isSingle && firstBonifiableListing) {
      navigateInCurrentNavigatorWithFallback(navigation2, 'one-listing', 'management', {
        id: firstBonifiableListing.id,
      });
    } else {
      navigateInCurrentNavigatorWithFallback(navigation1, 'my-listings', 'management', {
        type: ListingType.offer,
      });
    }
  };

  const dismissBanner = () => {
    setClosedBonificationBanner(true);
    setDismissed(true);
  };

  const isInBanner = route.name === 'landing-page';
  const isDesktopLandingPage = isInBanner && isLargeUI;
  const compactLayout = getCompactPromptLayout(route.name, isLargeUI, isSmallUI, dismissable);

  if (isDesktopLandingPage) {
    return (
      <Column
        style={{
          backgroundColor: theme.colors['surface.warning.subtle'],
          borderRadius: theme.borderRadii['radius.lg'],
          minHeight: 198,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
          width: 416,
        }}
      >
        {dismissable ? (
          <Button
            accessibilityLabel={t('close', { ns: 'accessibility' })}
            variant="plain"
            onPress={dismissBanner}
            style={{ position: 'absolute', right: 16, top: 18, zIndex: 1 }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.secondary']} />
          </Button>
        ) : null}

        <Column gap="spacing.general.x4" padding="spacing.general.x6">
          <Column gap="spacing.general.x3">
            <Row alignItems="center" gap="spacing.general.x2">
              <Column
                style={{
                  backgroundColor: theme.colors['surface.warning.subtle'],
                  borderRadius: 999,
                  paddingHorizontal: 7,
                  paddingVertical: 2,
                }}
              >
                <IconNotes strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
              </Column>
              <Text fontWeight="700" style={{ flex: 1, fontSize: 18, lineHeight: 20 }}>
                {t('bonificationPrompt.title', { ns: 'all-listings', count, numListings: count })}
              </Text>
            </Row>

            <Text style={{ color: theme.colors['text.primary'], fontSize: 16, lineHeight: 22 }}>
              <Trans
                components={{ strong: <Text fontWeight="700" /> }}
                i18nKey="bonificationPrompt.text"
                ns="all-listings"
                values={{ count, numListings: count }}
              />
            </Text>
          </Column>

          <Pressable
            accessibilityLabel={t('all-listings:bonificationPrompt.button', { count, numListings: count })}
            accessibilityRole="button"
            onPress={onPress}
            style={{
              alignItems: 'center',
              alignSelf: 'flex-start',
              backgroundColor: theme.colors['text.primary'],
              borderRadius: 999,
              height: 38,
              justifyContent: 'center',
              minWidth: 179,
              paddingHorizontal: theme.spacing['spacing.general.x6'],
            }}
          >
            <Text color="surface.default" fontWeight="600" style={{ fontSize: 16, lineHeight: 16 }}>
              {t('all-listings:bonificationPrompt.button', { numListings: count, count })}
            </Text>
          </Pressable>
        </Column>
      </Column>
    );
  }

  return (
    <Column
      gap={compactLayout.gap}
      paddingHorizontal="spacing.general.x4"
      paddingBottom={compactLayout.paddingBottom}
      backgroundColor="surface.warning.subtle"
      borderRadius="radius.lg"
      flex={compactLayout.flex}
      marginBottom={compactLayout.marginBottom}
      marginHorizontal={compactLayout.marginHorizontal}
      width={compactLayout.width}
      overflow={compactLayout.overflow}
    >
      {dismissable ? (
        <Button
          accessibilityLabel={t('close', { ns: 'accessibility' })}
          variant="plain"
          onPress={dismissBanner}
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
        >
          <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.secondary']} />
        </Button>
      ) : null}

      <Row
        alignItems="center"
        gap="spacing.general.x2"
        paddingRight={compactLayout.paddingRight}
        paddingTop={compactLayout.paddingTop}
      >
        <Column
          style={{
            backgroundColor: theme.colors['surface.warning.subtle'],
            borderRadius: theme.borderRadii['radius.sm'],
            padding: 8,
          }}
        >
          <IconNotes strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
        </Column>
        <Text fontWeight="700" variant="bodyLarge">
          {t('bonificationPrompt.title', { ns: 'all-listings', count, numListings: count })}
        </Text>
      </Row>

      <Text variant="bodyBase">
        <Trans
          components={{ strong: <Text fontWeight="700" /> }}
          i18nKey="bonificationPrompt.text"
          ns={compactLayout.bodyNamespace}
          values={{ count, numListings: count }}
        />
      </Text>

      {route.name === 'my-listings' && pendingBonifications && pendingBonifications.length > 0 ? (
        <Column gap="spacing.general.x2">
          <Separator height={1} style={{ marginBottom: theme.spacing['spacing.general.x2'] }} />
          <Text variant="bodyBase" color="text.secondary" fontWeight="600">
            {t('pendingBonificationsBanner.title', { ns: 'all-listings', count: pendingBonifications.length })}
          </Text>
          {pendingBonifications.map((bonification, index) => (
            <Pressable
              key={bonification.id}
              onPress={() => navigateToBonification(bonification)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
                backgroundColor: theme.colors['surface.default'],
                borderRadius: theme.borderRadii['radius.sm'],
                padding: theme.spacing['spacing.general.x2'],
              }}
            >
              <Row flex={1} alignItems="center" gap="spacing.general.x4">
                <PCImage
                  aspectRatio={1}
                  height={40}
                  source={{
                    uri: pendingBonifications[index]?.listing.picture ?? bonification.suggestedPicture ?? undefined,
                  }}
                  style={{ borderRadius: theme.borderRadii['radius.xl'] }}
                />
                <Text fontWeight="600" numberOfLines={1}>
                  {pendingBonifications[index]?.listing.item.name ?? t('pendingBonificationsBanner.item')}
                </Text>
              </Row>
              <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.secondary']} />
            </Pressable>
          ))}
        </Column>
      ) : null}

      {route.name === 'landing-page' ? (
        <Button onPress={onPress} variant="primary">
          {t('all-listings:bonificationPrompt.button', { count, numListings: count })}
        </Button>
      ) : null}
    </Column>
  );
}

function BonificationPromptWithState({
  dismissable,
  setClosedBonificationBanner,
  setDismissed,
  numberOfListingsToBonify,
}: Pick<
  BonificationBannerState,
  'dismissable' | 'setClosedBonificationBanner' | 'setDismissed' | 'numberOfListingsToBonify'
>) {
  return (
    <BonificationPromptContent
      count={numberOfListingsToBonify}
      dismissable={dismissable}
      setClosedBonificationBanner={setClosedBonificationBanner}
      setDismissed={setDismissed}
    />
  );
}

function BonificationPromptWithHook() {
  const bannerState = useBonificationBanner();
  if (bannerState.shouldHideBonificationBanner) return null;
  return (
    <Animated.View exiting={FadeOut.duration(180)} layout={LinearTransition.duration(250)}>
      <BonificationPromptWithState {...bannerState} />
    </Animated.View>
  );
}

export function BonificationPrompt({ bannerState }: { bannerState?: BonificationBannerState }) {
  if (bannerState) {
    if (bannerState.shouldHideBonificationBanner) return null;
    return <BonificationPromptWithState {...bannerState} />;
  }
  return <BonificationPromptWithHook />;
}

export function BonificationBadge({ height = 30, width }: { height?: number; width?: number }) {
  const { t } = useTranslation('all-listings');
  const route = useRoute();

  return (
    <Row
      alignItems="center"
      gap="spacing.general.x1"
      style={{
        backgroundColor: theme.colors['surface.warning.subtle'],
        borderRadius: theme.borderRadii['radius.full'],
        paddingHorizontal: theme.spacing['spacing.general.x2'],
        paddingVertical: theme.spacing['spacing.general.x1'],
        height: height,
        width: width,
      }}
    >
      <IconNotes strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} />
      <Text color="text.primary" fontSize={13} fontWeight="500">
        {t(route.name === 'new' ? 'bonificationBadge2' : 'bonificationBadge')}
      </Text>
    </Row>
  );
}
